/**
 * Laravel Echo Service - WebSocket usando protocolo Pusher
 * 
 * Substitui o websocketService.ts antigo para usar Laravel Echo
 * Protocolo: Pusher (compatível com Laravel WebSockets)
 */

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Configuração do Pusher para Laravel WebSockets
const getPusherConfig = () => {
  // Determinar API URL baseado no ambiente
  let apiUrl: string;
  
  if (process.env.REACT_APP_API_URL) {
    apiUrl = process.env.REACT_APP_API_URL;
  } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // Desenvolvimento local: sempre usar localhost:8000
    apiUrl = 'http://localhost:8000/api';
  } else {
    // Produção: usar mesma origem
    apiUrl = `${window.location.origin}/api`;
  }
  
  // Construir authEndpoint completo (URL absoluta)
  const authEndpoint = `${apiUrl}/broadcasting/auth`;
  
  // Validar que é uma URL absoluta
  if (!authEndpoint.startsWith('http://') && !authEndpoint.startsWith('https://')) {
    throw new Error(`authEndpoint deve ser uma URL absoluta: ${authEndpoint}`);
  }
  
  console.log('🔐 [CONFIG] API URL:', apiUrl);
  console.log('🔐 [CONFIG] Auth Endpoint:', authEndpoint);
  
  // Extrair host e porta da API URL para WebSocket
  let wsHost = '127.0.0.1';
  let wsPort = 6001;
  let wsScheme = 'ws';
  
  try {
    const url = new URL(apiUrl);
    wsHost = url.hostname;
    
    if (url.protocol === 'https:' || (url.hostname !== 'localhost' && url.hostname !== '127.0.0.1')) {
      wsScheme = 'wss';
      wsPort = 8443;
    } else {
      wsScheme = 'ws';
      wsPort = 6001;
    }
  } catch (error) {
    console.error('Erro ao parsear API URL:', error);
    // Manter valores padrão
  }

  const pusherConfig = {
    broadcaster: 'pusher' as const,
    key: 'local-key',
    wsHost: wsHost,
    wsPort: wsPort,
    wssPort: wsPort,
    forceTLS: wsScheme === 'wss',
    encrypted: wsScheme === 'wss',
    enabledTransports: ['ws', 'wss'] as const,
    disableStats: true,
    authEndpoint: authEndpoint, // URL completa e correta
  };

  return pusherConfig;
};

let echoInstance: Echo<any> | null = null;

/**
 * Inicializar Laravel Echo
 */
export const initLaravelEcho = (): Echo<any> => {
  if (echoInstance) {
    return echoInstance;
  }

  // Configurar Pusher para usar WebSocket customizado
  (window as any).Pusher = Pusher;

  const config = getPusherConfig();
  
  // Capturar authEndpoint em uma constante para garantir que seja usado corretamente
  const authEndpoint = config.authEndpoint;
  
  // Validar authEndpoint
  if (!authEndpoint || (!authEndpoint.startsWith('http://') && !authEndpoint.startsWith('https://'))) {
    throw new Error(`authEndpoint deve ser uma URL absoluta: ${authEndpoint}`);
  }
  
  console.log('🔐 [INIT] Auth Endpoint:', authEndpoint);
  
  // Configurar Pusher client
  if (!config.wsHost || !config.wsPort) {
    throw new Error(`Configuração inválida: wsHost=${config.wsHost}, wsPort=${config.wsPort}`);
  }
  
  // Configuração do Pusher baseada na documentação oficial
  // https://beyondco.de/docs/laravel-websockets/basic-usage/pusher
  const pusherClientConfig: any = {
    wsHost: config.wsHost,
    wsPort: config.wsPort,
    wssPort: config.wssPort,
    forceTLS: config.forceTLS,
    encrypted: config.forceTLS,
    enabledTransports: config.forceTLS ? ['wss', 'ws'] : ['ws', 'wss'],
    disableStats: config.disableStats,
    cluster: 'mt1', // Dummy - será ignorado porque wsHost está especificado
    // IMPORTANTE: Usar 'authorizer' (não 'channelAuthorizer') no Pusher-js 8.4.0
    // O Pusher converte 'authorizer' para customHandler no channelAuthorization
    authorizer: (channel: any, options: any) => {
      return {
        authorize: (socketId: string, callback: any) => {
          console.log('🔐 [AUTHORIZER] Autorizando canal:', channel.name);
          console.log('🔐 [AUTHORIZER] Socket ID:', socketId);
          console.log('🔐 [AUTHORIZER] URL:', authEndpoint);
          
          const xhr = new XMLHttpRequest();
          xhr.open('POST', authEndpoint, true);
          xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.withCredentials = true; // CRÍTICO: Enviar cookies
          
          xhr.onreadystatechange = function() {
            if (xhr.readyState === 4) {
              console.log('🔐 [AUTHORIZER] Resposta:', xhr.status);
              if (xhr.status === 200) {
                try {
                  const response = JSON.parse(xhr.responseText);
                  callback(null, response);
                } catch (e) {
                  callback(new Error('Invalid JSON response'), null);
                }
              } else {
                callback(new Error(`Auth failed: ${xhr.status}`), null);
              }
            }
          };
          
          xhr.onerror = function() {
            callback(new Error('Network error'), null);
          };
          
          const formData = `socket_id=${encodeURIComponent(socketId)}&channel_name=${encodeURIComponent(channel.name)}`;
          console.log('🔐 [AUTHORIZER] Enviando para:', authEndpoint);
          xhr.send(formData);
        },
      };
    },
  };
  
  // IMPORTANTE: Quando forceTLS é true, o Pusher usa httpsPort em vez de wssPort
  // Por isso, precisamos também especificar httpsPort para garantir que use a porta correta
  if (config.forceTLS) {
    pusherClientConfig.httpsPort = config.wssPort; // Garantir que use a porta 8443 para HTTPS/WSS
  }
  
  // IMPORTANTE: Laravel WebSockets usa /app/{appKey} como path completo
  // O Pusher constrói a URL como: wss://hostname:port/httpPath/app/key
  // Por padrão, o Pusher usa '/pusher' como httpPath, resultando em: wss://hostname:port/pusher/app/key (ERRADO)
  // Para Laravel WebSockets, precisamos que o path seja /app/key, então httpPath deve ser '/'
  // Quando httpPath='/', o Pusher constrói: wss://hostname:port/app/key (CORRETO!)
  pusherClientConfig.httpPath = '/';
  
  console.log('🔐 [PUSHER CONFIG] Configuração:', {
    wsHost: pusherClientConfig.wsHost,
    wsPort: pusherClientConfig.wsPort,
    authEndpoint: authEndpoint,
    hasChannelAuthorizer: typeof pusherClientConfig.channelAuthorizer === 'function',
  });
  
  // IMPORTANTE: Desabilitar conexão automática para ter controle sobre quando conectar
  // O Pusher pode estar tentando conectar automaticamente e falhando antes de podermos configurar listeners
  pusherClientConfig.enableLogging = true; // Habilitar logging para debug
  
  // Criar instância do Pusher primeiro para verificar se o channelAuthorizer está sendo criado
  let pusherClient: any;
  try {
    pusherClient = new Pusher(config.key, pusherClientConfig);
    console.log('✅ Pusher client criado');
  } catch (error) {
    console.error('❌ Erro ao criar Pusher:', error);
    throw error;
  }
  
  // Verificar configuração do Pusher
  console.log('🔐 [PUSHER] Configuração:', {
    hasAuthorizer: typeof pusherClientConfig.authorizer === 'function',
    wsHost: pusherClient.config?.wsHost,
    wsPort: pusherClient.config?.wsPort,
  });
  
  // Adicionar listener para capturar erros imediatamente
  pusherClient.connection.bind('error', (error: any) => {
    console.error('❌ [IMMEDIATE ERROR] Erro capturado imediatamente após criar Pusher:', error);
    console.error('❌ [IMMEDIATE ERROR] Tipo:', typeof error);
    console.error('❌ [IMMEDIATE ERROR] String:', String(error));
    console.error('❌ [IMMEDIATE ERROR] Error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
  });
  
  pusherClient.connection.bind('state_change', (states: any) => {
    console.log('🔄 [STATE CHANGE]', states.previous, '->', states.current);
    
    // Log detalhado de TODOS os state changes para debug
    const connection = pusherClient.connection;
    if (connection) {
      console.log('🔄 [STATE CHANGE DETAILS]', {
        previous: states.previous,
        current: states.current,
        state: connection.state,
        error: connection.error,
        socket_id: connection.socket_id,
        transport: (connection as any).transport?.name || 'N/A',
        key: connection.key,
      });
    }
    
    if (states.current === 'failed') {
      console.error('❌ [STATE CHANGE TO FAILED] Pusher falhou!');
      console.error('❌ [STATE CHANGE TO FAILED] Estado anterior:', states.previous);
      console.error('❌ [STATE CHANGE TO FAILED] Estado atual:', states.current);
      console.error('❌ [STATE CHANGE TO FAILED] Erro:', connection?.error);
      
      // Tentar obter mais informações sobre o erro
      if (connection) {
        console.error('❌ [STATE CHANGE TO FAILED] Connection object completo:', {
          state: connection.state,
          error: connection.error,
          socket_id: connection.socket_id,
          activityTimeout: connection.activityTimeout,
          key: connection.key,
          transport: (connection as any).transport,
          transportName: (connection as any).transport?.name,
          transportState: (connection as any).transport?.state,
        });
        
        // Tentar acessar o erro interno do transporte
        const transport = (connection as any).transport;
        if (transport) {
          console.error('❌ [STATE CHANGE TO FAILED] Transport details:', {
            name: transport.name,
            state: transport.state,
            error: transport.error,
            socket: transport.socket,
            url: transport.url,
          });
        }
      }
    }
  });
  
  // Tentar forçar conexão manualmente para ver o erro
  setTimeout(() => {
    console.log('🔍 [DEBUG] Tentando conectar manualmente...');
    console.log('🔍 [DEBUG] Estado antes de conectar:', pusherClient.connection?.state);
    console.log('🔍 [DEBUG] Config antes de conectar:', {
      wsHost: pusherClient.config?.wsHost,
      wsPort: pusherClient.config?.wsPort,
      wssPort: pusherClient.config?.wssPort,
      httpsPort: pusherClient.config?.httpsPort,
      httpHost: pusherClient.config?.httpHost,
      httpPort: pusherClient.config?.httpPort,
      httpPath: pusherClient.config?.httpPath,
      forceTLS: pusherClient.config?.forceTLS,
      enabledTransports: pusherClient.config?.enabledTransports,
    });
    try {
      pusherClient.connect();
      console.log('🔍 [DEBUG] connect() chamado com sucesso');
      // Verificar estado imediatamente após chamar connect
      setTimeout(() => {
        console.log('🔍 [DEBUG] Estado após connect (100ms):', pusherClient.connection?.state);
        console.log('🔍 [DEBUG] Erro após connect:', pusherClient.connection?.error);
      }, 100);
    } catch (error) {
      console.error('❌ [DEBUG] Erro ao chamar connect():', error);
    }
  }, 200);
  
  // Verificar imediatamente o estado da conexão após criar
  setTimeout(() => {
    console.log('🔐 [AFTER CREATION] Estado da conexão Pusher:', pusherClient.connection?.state);
    console.log('🔐 [AFTER CREATION] Erro na conexão:', pusherClient.connection?.error);
    if (pusherClient.connection?.state === 'failed') {
      console.error('❌ [AFTER CREATION] Pusher falhou imediatamente após criação!');
      console.error('❌ [AFTER CREATION] Verifique a configuração do Pusher');
      // Tentar ver qual URL o Pusher está tentando usar
      console.error('❌ [AFTER CREATION] Pusher config:', pusherClient.config);
    }
  }, 100);
  
  // Criar configuração do Echo
  // O authEndpoint e channelAuthorizer já estão configurados no Pusher client
  const echoConfig: any = {
    broadcaster: config.broadcaster,
    key: config.key,
    client: pusherClient,
  };
  
  echoInstance = new Echo(echoConfig);

  console.log('✅ Laravel Echo inicializado');
  console.log('✅ Configuração:', {
    broadcaster: config.broadcaster,
    key: config.key,
    authEndpoint: config.authEndpoint,
    wsHost: config.wsHost,
    wsPort: config.wsPort,
  });
  console.log('✅ Echo options:', {
    authEndpoint: echoInstance.options?.authEndpoint,
    broadcaster: echoInstance.options?.broadcaster,
  });
  return echoInstance;
};

/**
 * Obter instância do Echo
 */
export const getEcho = (): Echo<any> => {
  if (!echoInstance) {
    return initLaravelEcho();
  }
  return echoInstance;
};

/**
 * Desconectar Echo
 */
export const disconnectEcho = () => {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
    console.log('Laravel Echo desconectado');
  }
};

/**
 * Inscrever-se em uma conversa
 */
export const subscribeToConversation = (
  conversationId: number,
  callback: (message: any) => void
): (() => void) => {
  const echo = getEcho();
  
  console.log(`📡 Laravel Echo: Inscrevendo-se na conversa ${conversationId}`);
  
  // Canal privado: Laravel Echo adiciona automaticamente prefixo "private-"
  // Então echo.private('conversation.1') cria o canal "private-conversation.1"
  // IMPORTANTE: Quando você chama echo.private(), o Pusher automaticamente tenta autenticar
  // fazendo uma requisição POST para o authEndpoint configurado
  console.log(`🔐 Tentando se inscrever no canal privado: conversation.${conversationId}`);
  console.log(`🔐 Echo instance:`, echo);
  console.log(`🔐 Echo options:`, echo.options);
  console.log(`🔐 authEndpoint configurado:`, echo.options?.authEndpoint || 'NÃO CONFIGURADO!');
  console.log(`🔐 Pusher client:`, (echo as any).connector?.pusher);
  console.log(`🔐 Pusher client options:`, (echo as any).connector?.pusher?.config);
  
  const pusher = (echo as any).connector?.pusher;
  
  // Função para se inscrever no canal (definida antes de ser usada)
  const subscribeToChannel = () => {
    console.log('🔐 [subscribeToChannel] Iniciando inscrição no canal...');
    console.log('🔐 [subscribeToChannel] Estado do Pusher:', pusher?.connection?.state);
    
    try {
      console.log('🔐 [subscribeToChannel] Criando canal privado...');
      const channel = echo.private(`conversation.${conversationId}`);
      console.log(`🔐 [subscribeToChannel] Canal criado:`, channel.name);
      
      // Verificar se o canal está tentando autenticar
      console.log('🔐 [subscribeToChannel] Verificando se canal precisa autenticar...');
      
      // Adicionar listener para eventos de autenticação
      channel.listen('pusher:subscription_succeeded', () => {
        console.log('✅ [subscribeToChannel] Inscrição bem-sucedida no canal:', channel.name);
      });
      
      channel.listen('pusher:subscription_error', (error: any) => {
        console.error('❌ [subscribeToChannel] Erro na inscrição:', error);
      });
    
    // Verificar se o canal tem autorização configurada
    if (pusher) {
      console.log('🔐 Verificando autorização do canal...');
      console.log('🔐 Pusher config.channelAuthorizer:', pusher.config?.channelAuthorizer ? 'EXISTE' : 'NÃO EXISTE');
      
      // Tentar verificar se o canal está tentando autorizar
      const channelInstance = pusher.channels?.find(channel.name);
      if (channelInstance) {
        console.log('🔐 Instância do canal encontrada:', channelInstance.name);
        console.log('🔐 Canal tem método authorize?', typeof channelInstance.authorize === 'function');
        
        // Interceptar o método authorize para ver quando é chamado
        if (typeof channelInstance.authorize === 'function') {
          const originalAuthorize = channelInstance.authorize.bind(channelInstance);
          channelInstance.authorize = function(socketId: string, callback: any) {
            console.log('🔐 [AUTORIZAÇÃO] Método authorize chamado!');
            console.log('🔐 [AUTORIZAÇÃO] Socket ID:', socketId);
            console.log('🔐 [AUTORIZAÇÃO] Channel:', channelInstance.name);
            console.log('🔐 [AUTORIZAÇÃO] Chamando originalAuthorize...');
            
            const result = originalAuthorize(socketId, (error: any, authData: any) => {
              if (error) {
                console.error('❌ [AUTORIZAÇÃO] Erro na autenticação:', error);
              } else {
                console.log('✅ [AUTORIZAÇÃO] Autenticação bem-sucedida:', authData);
              }
              if (callback) {
                callback(error, authData);
              }
            });
            
            return result;
          };
        }
      }
    }
    
    // Escutar evento 'message.sent' (nome do evento definido em MessageSent::broadcastAs())
    // O Laravel adiciona automaticamente um ponto no início quando usa broadcastAs()
    // Então o evento é ".message.sent" no frontend
    channel.listen('.message.sent', (data: any) => {
      console.log('📥 Laravel Echo: Mensagem recebida no canal', channel.name, data);
      if (callback) {
        callback(data);
      }
    });
    
    // Escutar também eventos de conexão para debug
    channel.listen('pusher:subscription_succeeded', () => {
      console.log('✅ Laravel Echo: Inscrito com sucesso no canal', channel.name);
    });
    
    channel.listen('pusher:subscription_error', (error: any) => {
      console.error('❌ Laravel Echo: Erro ao se inscrever no canal', channel.name, error);
      console.error('❌ Detalhes do erro:', JSON.stringify(error, null, 2));
    });
    
    // Escutar evento de autenticação
    channel.listen('pusher:subscription_auth', (data: any) => {
      console.log('🔐 Laravel Echo: Autenticação do canal recebida', channel.name, data);
    });
    
    console.log('🔐 [subscribeToChannel] Listeners configurados para o canal', channel.name);
    } catch (error) {
      console.error('❌ [subscribeToChannel] Erro ao criar canal:', error);
    }
  };
  
  // Verificar estado da conexão WebSocket
  if (pusher) {
    console.log('🔐 Estado da conexão Pusher:', pusher.connection?.state || 'desconhecido');
    console.log('🔐 Pusher está conectado?', pusher.connection?.state === 'connected');
    console.log('🔐 Pusher connection object:', pusher.connection);
    
    // Adicionar listeners para todos os eventos de conexão para debug
    pusher.connection.bind('state_change', (states: any) => {
      console.log('🔄 [PUSHER STATE CHANGE]', states.previous, '->', states.current);
      if (states.current === 'connected') {
        console.log('✅ [STATE CHANGE] WebSocket conectado, tentando se inscrever...');
        subscribeToChannel();
      } else if (states.current === 'unavailable' || states.current === 'failed') {
        console.error('❌ [STATE CHANGE] WebSocket não disponível ou falhou:', states.current);
        console.error('❌ Estado anterior:', states.previous);
        console.error('❌ Pusher connection error:', pusher.connection?.error);
        console.error('❌ Pusher connection socket_id:', pusher.connection?.socket_id);
        console.error('❌ Verifique se o servidor WebSocket está rodando e acessível');
      }
    });
    
    pusher.connection.bind('connected', () => {
      console.log('✅ [PUSHER CONNECTED] WebSocket conectado!');
      subscribeToChannel();
    });
    
    pusher.connection.bind('disconnected', () => {
      console.log('❌ [PUSHER DISCONNECTED] WebSocket desconectado');
    });
    
    pusher.connection.bind('error', (error: any) => {
      console.error('❌ [PUSHER ERROR] Erro na conexão WebSocket:', error);
      console.error('❌ Tipo do erro:', typeof error);
      console.error('❌ Erro como string:', String(error));
      console.error('❌ Erro como objeto:', error);
      try {
        console.error('❌ Detalhes do erro (JSON):', JSON.stringify(error, null, 2));
      } catch (e) {
        console.error('❌ Não foi possível serializar o erro:', e);
      }
      console.error('❌ Pusher config no momento do erro:', {
        wsHost: pusher.config.wsHost,
        wsPort: pusher.config.wsPort,
        wssPort: pusher.config.wssPort,
        httpsPort: pusher.config.httpsPort,
        httpPath: pusher.config.httpPath,
        forceTLS: pusher.config.forceTLS,
        encrypted: pusher.config.encrypted,
        enabledTransports: pusher.config.enabledTransports,
        useTLS: pusher.config.useTLS,
      });
      
      // Tentar construir a URL exata
      const protocol = pusher.config.forceTLS ? 'wss' : 'ws';
      const port = pusher.config.forceTLS ? (pusher.config.httpsPort || pusher.config.wssPort) : pusher.config.wsPort;
      const path = (pusher.config.httpPath || '/pusher').replace(/\/+$/, ''); // Remover barras duplicadas
      const url = `${protocol}://${pusher.config.wsHost}:${port}${path}/app/${pusher.key}`;
      console.error('❌ URL tentada:', url);
      
      // Verificar se está tentando usar ws:// quando deveria ser wss://
      if (!pusher.config.forceTLS && port === 8443) {
        console.error('❌ PROBLEMA: Pusher está tentando usar ws:// na porta 8443 (SSL)!');
        console.error('❌ forceTLS deve ser true quando usando porta 8443 com SSL');
      }
    });
    
    pusher.connection.bind('unavailable', () => {
      console.error('❌ [PUSHER UNAVAILABLE] WebSocket não disponível');
      console.error('❌ Pusher config completo:', {
        wsHost: pusher.config.wsHost,
        wsPort: pusher.config.wsPort,
        wssPort: pusher.config.wssPort,
        httpsPort: pusher.config.httpsPort,
        httpPath: pusher.config.httpPath,
        httpPort: pusher.config.httpPort,
        key: pusher.key,
        forceTLS: pusher.config.forceTLS,
        enabledTransports: pusher.config.enabledTransports,
      });
      
      // Tentar construir a URL exata que o Pusher está usando
      const protocol = pusher.config.forceTLS ? 'wss' : 'ws';
      const port = pusher.config.forceTLS ? (pusher.config.httpsPort || pusher.config.wssPort) : pusher.config.wsPort;
      const path = (pusher.config.httpPath || '/pusher').replace(/\/+$/, ''); // Remover barras duplicadas no final
      const url = `${protocol}://${pusher.config.wsHost}:${port}${path}/app/${pusher.key}`;
      console.error('❌ URL que o Pusher está tentando usar:', url);
      console.error('❌ Configuração do Pusher:', {
        forceTLS: pusher.config.forceTLS,
        encrypted: pusher.config.encrypted,
        wsPort: pusher.config.wsPort,
        wssPort: pusher.config.wssPort,
        httpsPort: pusher.config.httpsPort,
        httpPath: pusher.config.httpPath,
      });
      
      console.error('❌ Verifique:');
      console.error('   1. Se o servidor WebSocket está rodando na porta 6001');
      console.error('   2. Se o nginx está fazendo proxy da porta 8443 para localhost:6001');
      console.error('   3. Se há erros de CORS ou SSL');
      console.error('   4. Se a porta 8443 está acessível e não bloqueada por firewall');
      console.error('   5. Abra o DevTools > Network > WS e veja qual URL está sendo tentada');
    });
    
    // Aguardar conexão se não estiver conectado
    if (pusher.connection?.state !== 'connected') {
      console.log('⏳ Aguardando conexão WebSocket antes de se inscrever no canal...');
      console.log('⏳ Estado atual:', pusher.connection?.state);
      console.log('⏳ Erro na conexão:', pusher.connection?.error);
      
      // Se está em estado 'failed', tentar reconectar
      if (pusher.connection?.state === 'failed') {
        console.log('🔄 Estado é "failed", tentando reconectar...');
        console.log('🔄 Erro que causou o failed:', pusher.connection?.error);
        // Limpar o estado e tentar conectar novamente
        pusher.disconnect();
        setTimeout(() => {
          console.log('🔄 Reconectando Pusher após 1 segundo...');
          pusher.connect();
        }, 1000);
      } else if (pusher.connection?.state === 'disconnected' || pusher.connection?.state === 'unavailable') {
        console.log('🔌 Conectando Pusher...');
        pusher.connect();
      }
      
      // Aguardar evento de conexão
      const onConnected = () => {
        console.log('✅ [ON_CONNECTED] WebSocket conectado, agora tentando se inscrever no canal');
        subscribeToChannel();
      };
      
      pusher.connection.bind('connected', onConnected);
      
      // Verificar periodicamente se conectou (fallback)
      let checkCount = 0;
      const checkInterval = setInterval(() => {
        checkCount++;
        console.log(`⏳ [CHECK ${checkCount}] Estado da conexão:`, pusher.connection?.state);
        if (pusher.connection?.state === 'connected') {
          console.log('✅ [CHECK] WebSocket conectado (verificação periódica), tentando se inscrever...');
          clearInterval(checkInterval);
          subscribeToChannel();
        } else if (checkCount >= 10) {
          console.warn('⚠️ [CHECK] Timeout aguardando conexão, tentando se inscrever mesmo assim...');
          clearInterval(checkInterval);
          subscribeToChannel();
        }
      }, 500);
      
      return () => {
        clearInterval(checkInterval);
        pusher.connection.unbind('connected', onConnected);
        echo.leave(`conversation.${conversationId}`);
        console.log(`📡 Laravel Echo: Cancelando inscrição na conversa ${conversationId}`);
      };
    } else {
      // Já está conectado, pode se inscrever imediatamente
      console.log('✅ WebSocket já conectado, se inscrevendo no canal...');
      subscribeToChannel();
    }
  } else {
    // Pusher não disponível, tentar mesmo assim
    console.warn('⚠️ Pusher não disponível, tentando se inscrever mesmo assim...');
    subscribeToChannel();
  }

  // Retornar função para cancelar inscrição
  return () => {
    // Laravel Echo adiciona automaticamente o prefixo "private-", então usar apenas "conversation.{id}"
    echo.leave(`conversation.${conversationId}`);
    console.log(`📡 Laravel Echo: Cancelando inscrição na conversa ${conversationId}`);
  };
};


/**
 * Inscrever-se em notificações do usuário
 */
export const subscribeToUserNotifications = (
  userId: number,
  callback: (notification: any) => void
): (() => void) => {
  const echo = getEcho();
  
  console.log(`📡 Laravel Echo: Inscrevendo-se em notificações do usuário ${userId}`);
  
  // Canal privado: private-user.{id}
  const channel = echo.private(`user.${userId}`);
  
  // Escutar evento 'notification.created'
  channel.listen('.notification.created', (data: any) => {
    console.log('📥 Laravel Echo: Notificação recebida', data);
    if (callback) {
      callback(data);
    }
  });

  // Retornar função para cancelar inscrição
  return () => {
    echo.leave(`private-user.${userId}`);
    console.log(`📡 Laravel Echo: Cancelando inscrição em notificações do usuário ${userId}`);
  };
};
